import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useSystemSettings } from '../hooks/useSystemSettings';

const advancedSchema = z.object({
  customCss: z.string().optional(),
  customHeaderScripts: z.string().optional(),
  customBodyScripts: z.string().optional(),
  debugMode: z.boolean(),
  enableExperimentalFeatures: z.boolean(),
});

type AdvancedFormValues = z.infer<typeof advancedSchema>;

interface AdvancedSectionProps {
  onChange?: (isDirty: boolean) => void;
  onSaveSuccess?: () => void;
}

export function AdvancedSection({ onChange, onSaveSuccess }: AdvancedSectionProps) {
  const { advanced, updateAdvanced } = useSystemSettings();

  const { register, handleSubmit, reset, formState: { isDirty } } = useForm<AdvancedFormValues>({
    resolver: zodResolver(advancedSchema),
    defaultValues: advanced || {}
  });

  // Sync data when loaded
  React.useEffect(() => {
    if (advanced) {
      reset(advanced);
    }
  }, [advanced, reset]);

  React.useEffect(() => {
    if (onChange) {
      onChange(isDirty);
    }
  }, [isDirty, onChange]);

  const onSubmit = async (data: AdvancedFormValues) => {
    try {
      await updateAdvanced(data);
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      // Error handled by mutation
    }
  };

  return (
    <form id="advanced-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      {/* Code Injections */}
      <div className="space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
            <i className="ri-css3-line text-blue-500" />
            Custom CSS
          </label>
          <textarea
            {...register('customCss')}
            rows={5}
            className="w-full font-mono text-xs bg-slate-900 text-slate-300 rounded-xl border border-slate-800 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-600/20 transition-all"
            placeholder="/* Chèn mã CSS tùy chỉnh vào đây... */"
          />
          <p className="text-[10px] text-slate-400">Mã CSS này sẽ được chèn vào thẻ &lt;style&gt; ở cuối phần &lt;head&gt;.</p>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
            <i className="ri-code-s-slash-line text-amber-500" />
            Header Scripts (JS)
          </label>
          <textarea
            {...register('customHeaderScripts')}
            rows={4}
            className="w-full font-mono text-xs bg-slate-900 text-slate-300 rounded-xl border border-slate-800 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-600/20 transition-all"
            placeholder="<script> // Google Analytics, v.v. </script>"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
            <i className="ri-terminal-window-line text-emerald-500" />
            Body Scripts (JS)
          </label>
          <textarea
            {...register('customBodyScripts')}
            rows={4}
            className="w-full font-mono text-xs bg-slate-900 text-slate-300 rounded-xl border border-slate-800 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-600/20 transition-all"
            placeholder="<script> // Scripts trước khi đóng thẻ body </script>"
          />
        </div>
      </div>

      {/* Experimental Toggles */}
      <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100 space-y-6">
        <h4 className="text-xs font-black text-slate-400  tracking-widest">Thiết lập hệ thống</h4>

        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-bold text-slate-900">Chế độ Debug (Gỡ lỗi)</p>
            <p className="text-xs text-slate-400">Hiển thị các thông báo kỹ thuật chi tiết trong Console trình duyệt.</p>
          </div>
          <input type="checkbox" {...register('debugMode')} className="w-5 h-5 rounded border-slate-300 text-brand-600 focus:ring-brand-600" />
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <div className="space-y-1">
            <p className="text-sm font-bold text-slate-900">Thử nghiệm (Experimental Features)</p>
            <p className="text-xs text-slate-400">Kích hoạt các tính năng mới đang trong giai đoạn phát triển (Beta).</p>
          </div>
          <input type="checkbox" {...register('enableExperimentalFeatures')} className="w-5 h-5 rounded border-slate-300 text-brand-600 focus:ring-brand-600" />
        </div>
      </div>

      {/* Danger Zone */}
      <div className="p-6 rounded-2xl border border-red-100 bg-red-50/30">
        <h4 className="text-sm font-black text-red-600  tracking-wider mb-2">Vùng nguy hiểm</h4>
        <p className="text-xs text-slate-500 mb-4">Các thao tác này có thể ảnh hưởng nghiêm trọng đến hoạt động của hệ thống.</p>
        <div className="flex gap-4">
          <button
            type="button"
            className="px-4 py-2 bg-white text-red-600 text-xs font-bold rounded-xl border border-red-200 hover:bg-red-50 transition-colors"
            onClick={() => alert('Chức năng Xóa Cache đang được phát triển')}
          >
            Xóa bộ nhớ đệm (Clear Cache)
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-white text-slate-400 text-xs font-bold rounded-xl border border-slate-200 cursor-not-allowed"
            disabled
          >
            Khôi phục cài đặt gốc
          </button>
        </div>
      </div>
    </form>
  );
}
